import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { JOURNEY_ASSET_BASE_URL } from "../config";
import JourneyPageRenderer from "./journeys/JourneyPageRenderer";
import JourneyStoryPrelude from "./journeys/JourneyStoryPrelude";
import ProfessorSwanGuide from "./journeys/ProfessorSwanGuide";
import { applySelfGuidedTeachingCopy } from "./journeys/journeySelfGuidedCopy";
import { primeReadAloud } from "./lessonPlans/present/useReadAloud";
import {
  getAuthoredJourneyPage,
  getAuthoredJourneyStepTitle,
} from "./journeys/journeyContentRegistry";
import {
  getInstructionalFlowModes,
  localizeInstructionalMode,
  localizeInstructionalModes,
  getInstructionalModeMeta,
} from "./journeys/journeyInstructionalModes";

import {
  Image,
  Modal,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

// Scroll offsets per journey step, keyed by "<journeySlug>:<stepId>". Kept at
// module scope because the whole panel unmounts while a step video or PWO
// takes over the stage; on remount the ScrollView restores from this cache.
const stepScrollOffsets = new Map();

// Nine-phase WonderLabs STEM Missions are the canonical lessons. In self-study
// mode only, keep their authored tasks/3D assets/answers exactly as-is but
// adapt teacher-dependent wording so Professor Swan is the first teaching
// contact; adult help is reserved for physical safety exceptions. Teacher-led
// pages still receive the original, unmodified authored content.
const HOME_STEM_MISSIONS = new Set([
  "electric-spiral-mission",
  "conductive-dough-mission",
  "wind-powered-car-mission",
  "robot-artist-mission",
]);

const HOME_COPY_FIELDS = new Set([
  "title", "subtitle", "eyebrow", "body", "prompt", "mission", "help",
  "label", "placeholder", "caption", "alt", "text", "reveal_text",
  "hidden_context", "reveal_policy", "student_cue", "suggested_questions",
  "incomplete_title", "complete_title", "incomplete_prompt", "complete_prompt",
]);

const forHomeStudent = (text) =>
  String(text)
    // Preserve the original kit readiness gates, but let the learner check
    // their own physical setup using the supplied guide and Professor Swan.
    // Never imply an AI can physically inspect, verify, or approve a circuit.
    .replace(/Need teacher help/gi, "Ask Professor Swan for help")
    .replace(/the teacher releases the kit/gi, "you finish the kit-readiness checks")
    .replace(/Exactly one teacher-approved AA battery/gi, "Exactly one kit-specified AA battery")
    .replace(/four teacher-approved AA batteries/gi, "four kit-specified AA batteries")
    .replace(/teacher-approved AA batteries/gi, "kit-specified AA batteries")
    .replace(/teacher- or family-approved paper/gi, "paper suitable for your work surface")
    .replace(/teacher- or family-approved/gi, "approved for your kit")
    .replace(/teacher-approved kit materials/gi, "supplied or kit-specified materials")
    .replace(/teacher-approved parts and batteries/gi, "kit-specified parts and batteries")
    .replace(/teacher-approved parts/gi, "kit-specified parts")
    .replace(/teacher-approved materials/gi, "kit-specified materials")
    .replace(/teacher-approved only/gi, "allowed only if included in the kit instructions")
    .replace(/teacher-approved first power sequence/gi, "checklist-based first power sequence")
    .replace(/teacher-approved test/gi, "checklist-based test")
    .replace(/teacher-approved power-off swap/gi, "checklist-based power-off swap")
    .replace(/teacher-prepared or cutting is supervised/gi, "ready; ask a grown-up if cutting is needed")
    .replace(/the teacher says the circuit is ready/gi, "you have completed the connection and safety checks")
    .replace(/the teacher approves your component inventory/gi, "you have completed your component inventory and safety checks")
    .replace(/A teacher should inspect before batteries are inserted/gi, "Check the assembly and safety list before inserting batteries; ask Professor Swan if any step is unclear")
    .replace(/Teacher inspects stability, clearance, and wiring before power/gi, "I checked stability, clearance, and wiring against the safety checklist before power")
    .replace(/Teacher confirms stable structure, blade clearance, and terminal contacts/gi, "I checked the structure, blade clearance, and contacts against the safety checklist")
    .replace(/Teacher approves or assists with swapping the two motor leads/gi, "I followed the power-off motor-lead swap checklist or asked Professor Swan for help")
    .replace(/with teacher approval/gi, "after completing the safety checklist")
    .replace(/teacher approval/gi, "completing the kit safety checklist")
    .replace(/teacher-controlled propeller demonstration/gi, "safe propeller demonstration from the supplied guide")
    .replace(/a teacher should approve the first powered test/gi, "complete the supplied safety checks before the first powered test")
    .replace(/the teacher about anything missing or damaged/gi, "Professor Swan about missing parts; ask a grown-up if a part is damaged")
    .replace(/stop and ask the teacher rather than forcing a part/gi, "do not force the part; ask Professor Swan to help compare the model and kit guide")
    .replace(/defer physical safety decisions to the teacher/gi, "use the physical safety checklist; stop and get an adult for damaged, hot, or unsafe components")
    .replace(/defer hands-on decisions to teacher/gi, "follow the physical guide and safety checklist, not remote approval")
    .replace(/teacher confirmation/gi, "learner checklist confirmation")
    .replace(/teacher\/reference check/gi, "check with Professor Swan and the supplied guide")
    .replace(/teacher-marked finish line/gi, "marked finish line")
    .replace(/teacher-marked lane/gi, "marked lane")
    .replace(/teacher-controlled/gi, "guided by the supplied safety checklist")
    .replace(/teacher-prepared/gi, "prepared according to the kit guide")
    .replace(/teacher supervises adhesive cutting/gi, "ask a grown-up if adhesive cutting is needed")
    .replace(/Keep physical assembly under teacher control/gi, "Use the supplied assembly guide and safety checklist; ask Professor Swan about unclear steps")
    .replace(/only teacher-approved/gi, "only included in the kit instructions")
    .replace(/\bteachers\b/gi, "Professor Swan")
    .replace(/\bteacher\b/gi, "Professor Swan")
    .replace(/\bSaras\b/g, "Professor Swan");

// Transform display text and checkpoint tutor context, never IDs, save_as,
// choice IDs, answer values, media paths, provenance, or completion rules.
const adaptBlockCopyForHome = (value, field = "") => {
  if (typeof value === "string") {
    return HOME_COPY_FIELDS.has(field) ? forHomeStudent(value) : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => adaptBlockCopyForHome(item, field));
  }
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key,
      adaptBlockCopyForHome(entry, key),
    ])
  );
};

const HOME_STEM_GUARDRAILS = {
  "electric-spiral-mission": "Professor Swan guides the learner first using the authored videos, rotatable PWOs, and printed build guide. Part A is a one-AA lamp/beacon; Part B uses a separate two-AA motor/fan kit. Require the authored safety, wiring, and blade-clearance checks before brief powered tests; switch power off for adjustments. Do not claim to inspect the physical circuit. Suggest an adult only for hot/damaged parts, cutting, or a step the child cannot safely perform.",
  "conductive-dough-mission": "Professor Swan guides the learner first using the supplied four-AA dough kit and existing 3D circuit guides. Keep dough out of mouths, do not add liquids, and switch off before changing connections. Avoid direct shorts; stop if a component warms or looks damaged and ask a grown-up. Do not give away the learner's predictions or observations.",
  "wind-powered-car-mission": "Professor Swan guides assembly and troubleshooting using the supplied rotatable PWO and printed rover instructions. Batteries power the motor and propeller; outdoor wind does not drive the rover. Complete the authored power-off and propeller-clearance checks, use a clear test lane, and switch off before adjustments. Ask an adult only if fastening needs tools the child cannot safely manage, or a component is hot/damaged. Do not claim to physically inspect parts.",
  "robot-artist-mission": "Professor Swan guides using the existing videos and 3D PWO. Protect the mystery: before its authored reveal, do not name the artist, motor, eccentric weight, or mechanism. After reveal, use the original stage-by-stage assembly guide, power-off adjustments, and protected drawing surface. Ask an adult only for hot/damaged parts or a physical task the child cannot safely perform. Do not reveal the student's answer or claim to inspect the physical build.",
};

const getJourneyAssetUrl = (imagePath) => {
  const cleanPath = String(imagePath || "").replace(/^\/+/, "");
  return `${JOURNEY_ASSET_BASE_URL}/${cleanPath}`;
};

const STEP_ONE_ARTWORKS = [
  {
    id: "A",
    title: "Artwork A",
    imagePath:
      "drawing-robot/step-1/artwork_a_black_spiral.png",
    accessibleDescription:
      "Black marker lines forming many irregular circular loops and spirals.",
    privateAiDescription:
      "Black irregular concentric loops and spirals, intended to resemble artwork produced by a simple drawing robot.",
  },
  {
    id: "B",
    title: "Artwork B",
    imagePath:
      "drawing-robot/step-1/artwork_b_color_loops.png",
    accessibleDescription:
      "Blue, purple, teal, and orange lines forming overlapping loops and circular patterns.",
    privateAiDescription:
      "Overlapping multicolored loops and circular patterns, intended to resemble artwork produced by a simple drawing robot.",
  },
  {
    id: "C",
    title: "Artwork C",
    imagePath:
      "drawing-robot/step-1/artwork_c_motion_paths.png",
    accessibleDescription:
      "Blue, purple, and green lines forming dense loops, waves, and wandering paths.",
    privateAiDescription:
      "Dense blue, purple, and green looping motion paths, intended to resemble artwork produced by a simple drawing robot.",
  },
  {
    id: "D",
    title: "Artwork D",
    imagePath:
      "drawing-robot/step-1/artwork_d_child_drawing.png",
    accessibleDescription:
      "A colorful picture of a house, tree, grass, chimney smoke, and sun.",
    privateAiDescription:
      "A childlike representational drawing of a house, tree, grass, and sun, intended to resemble a drawing made normally by a child.",
  },
];

const STEP_ONE_SUGGESTED_QUESTIONS = [
  "What clues should I look for?",
  "Help me compare Artworks A and D.",
  "Why do some of the lines repeat?",
  "How can I explain my theory using evidence?",
];

const STEP_ONE_HIDDEN_AI_CONTEXT = `
The student is completing Step 1, "The Mysterious Artwork," in the
Drawing Robot Learning Journey.

LEARNING PURPOSE:
Build curiosity, careful observation, comparison, and evidence-based
reasoning before revealing how the unusual artwork was produced.

VISIBLE ARTWORK:
- Artwork A: black irregular concentric loops and spirals
- Artwork B: overlapping multicolored loops and circular patterns
- Artwork C: dense blue, purple, and green looping motion paths
- Artwork D: a childlike drawing of a house, tree, grass, and sun

IMPORTANT PRIVATE INFORMATION:
Artworks A, B, and C are intended to resemble artwork produced by a
simple drawing robot kit. Artwork D is intended to resemble a drawing
made normally by a child.

DO NOT REVEAL:
- that a drawing robot created or could have created the artwork
- motors
- circuits
- electricity
- off-center weights
- rotation
- vibration
- the answer to the mystery

YOUR ROLE:
Act as a friendly fellow investigator.

Help the student:
- notice details
- compare artworks
- identify repeating patterns
- distinguish observation from guessing
- explain a theory using evidence

Ask one useful question at a time.

If the student asks for the answer, do not reveal it. Help them examine
the evidence.

If the student suggests a robot or machine, do not confirm or deny it.
Ask what visual evidence supports that theory.

Praise careful observations and reasoning rather than whether the
student's answer is correct.
`.trim();

function StepSectionTitle({ children, c, icon }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 9,
        marginBottom: 10,
      }}
    >
      <Text style={{ fontSize: 19 }}>{icon}</Text>
      <Text
        style={{
          color: c.text,
          fontSize: 20,
          fontWeight: "800",
          lineHeight: 26,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

const getJourneyPanelInstructionalPalette = (mode, c) => {
  const palette = c.instructionalModes?.[mode?.value];

  return (
    palette || {
      accent: c.accent,
      soft: c.accentSoft,
      border: c.border,
    }
  );
};

function LegacyInstructionalModeStrip({
  mode: modeValue,
  studentCue,
  c,
  t = (value) => value,
  marginTop = 0,
}) {
  const mode = localizeInstructionalMode(
    getInstructionalModeMeta(modeValue),
    t
  );

  if (!mode) return null;

  const palette = getJourneyPanelInstructionalPalette(mode, c);
  const cue = String(studentCue || "").trim();
  const accessibilityText = [
    mode.label,
    mode.actionLabel,
    cue,
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <View
      accessible
      accessibilityRole="text"
      accessibilityLabel={accessibilityText}
      style={{
        marginTop,
        marginBottom: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderLeftWidth: 5,
        borderColor: palette.border,
        borderLeftColor: palette.accent,
        backgroundColor: palette.soft,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 7,
        }}
      >
        <Text style={{ fontSize: 17 }}>{mode.icon}</Text>
        <Text
          style={{
            color: palette.accent,
            fontSize: 13,
            fontWeight: "900",
            letterSpacing: 0.7,
          }}
        >
          {mode.label}
        </Text>
        <Text
          style={{
            color: c.muted,
            fontSize: 13,
            fontWeight: "800",
          }}
        >
          {"—"} {mode.actionLabel}
        </Text>
      </View>

      {!!cue && (
        <Text
          style={{
            color: c.text,
            fontSize: 14,
            lineHeight: 20,
            fontWeight: "700",
            marginTop: 6,
          }}
        >
          {cue}
        </Text>
      )}
    </View>
  );
}

function LegacyInstructionalFlowSummary({ c, t = (value) => value }) {
  const modes = localizeInstructionalModes(
    getInstructionalFlowModes({
      summary: ["I_DO", "WE_DO", "YOU_DO"],
    }),
    t
  );

  return (
    <View
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${t("This step")}: ${modes
        .map((mode) => `${mode.label}, ${mode.actionLabel}`)
        .join(`; ${t("then")} `)}`}
      style={{
        marginTop: 18,
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: c.border,
        backgroundColor: c.panel,
      }}
    >
      <Text
        style={{
          color: c.muted,
          fontSize: 11,
          fontWeight: "900",
          letterSpacing: 0.8,
        }}
      >
        {t("THIS STEP")}
      </Text>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 9,
        }}
      >
        {modes.map((mode, index) => {
          const palette =
            getJourneyPanelInstructionalPalette(mode, c);

          return (
            <React.Fragment key={mode.value}>
              {index > 0 && (
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 16,
                    fontWeight: "900",
                  }}
                >
                  →
                </Text>
              )}

              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  paddingHorizontal: 10,
                  paddingVertical: 7,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: palette.border,
                  backgroundColor: palette.soft,
                }}
              >
                <Text style={{ fontSize: 15 }}>{mode.icon}</Text>
                <Text
                  style={{
                    color: palette.accent,
                    fontSize: 12,
                    fontWeight: "900",
                  }}
                >
                  {mode.label}
                </Text>
                <Text
                  style={{
                    color: c.text,
                    fontSize: 12,
                    fontWeight: "700",
                  }}
                >
                  {mode.actionLabel}
                </Text>
              </View>
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
}

function MysteriousArtworkStep({
  c,
  t,
  journey,
  step,
  observations,
  setObservations,
  selectedArtworkId,
  setSelectedArtworkId,
  zoomArtwork,
  setZoomArtwork,
  differentArtworkId,
  setDifferentArtworkId,
  differenceReason,
  setDifferenceReason,
  theory,
  setTheory,
  onAsk,
  onContextChange,
  readOnly = false,
}) {
  const selectedArtwork =
    STEP_ONE_ARTWORKS.find(
      (artwork) => artwork.id === selectedArtworkId
    ) || null;

  const contextPayload = useMemo(
    () => ({
      journey_id: journey?.journey_id ?? journey?.id ?? null,
      journey_slug:
        journey?.slug ??
        journey?.manifest?.slug ??
        null,

      step_id:
        step?.step_id ||
        "mysterious-artwork",

      step_title:
        step?.title ||
        "The Mysterious Artwork",

      hidden_context: STEP_ONE_HIDDEN_AI_CONTEXT,

      focus_item: selectedArtwork
        ? {
            kind: "artwork",
            id: selectedArtwork.id,
            label: selectedArtwork.title,
            asset_path: selectedArtwork.imagePath,
            private_ai_description:
              selectedArtwork.privateAiDescription,
          }
        : null,

      student_work: {
        observations: observations
          .map((value) => value.trim())
          .filter(Boolean),

        different_artwork_id:
          differentArtworkId,

        difference_reason:
          differenceReason.trim() || null,

        initial_theory:
          theory.trim() || null,
      },

      visual_context: selectedArtwork
        ? {
            type: "image",
            asset_path: selectedArtwork.imagePath,
            image_url: getJourneyAssetUrl(
              selectedArtwork.imagePath
            ),
            label: selectedArtwork.title,
          }
        : null,

      suggested_questions:
        STEP_ONE_SUGGESTED_QUESTIONS,

      reveal_policy:
        "Do not reveal the drawing robot or its science yet.",
    }),
    [
      journey,
      step,
      selectedArtwork,
      observations,
      differentArtworkId,
      differenceReason,
      theory,
    ]
  );

  useEffect(() => {
    onContextChange?.(contextPayload);
  }, [contextPayload, onContextChange]);

  const askSarasFromStepOne = (question) => {
    onAsk?.(question, contextPayload);
  };

  const updateObservation = (index, value) => {
    setObservations((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? value : item
      )
    );
  };

  const completedObservationCount = observations.filter(
    (value) => value.trim().length > 0
  ).length;

  return (
    <>
      <Modal
        visible={!!zoomArtwork}
        transparent
        animationType="fade"
        onRequestClose={() => setZoomArtwork(null)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: "rgba(15,23,42,0.86)",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <View
            style={{
              width: "100%",
              maxWidth: 920,
              borderRadius: 18,
              overflow: "hidden",
              backgroundColor: c.bg,
              borderWidth: 1,
              borderColor: c.border,
            }}
          >
            <View
              style={{
                paddingHorizontal: 18,
                paddingVertical: 13,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottomWidth: 1,
                borderBottomColor: c.border,
              }}
            >
              <Text style={{ color: c.text, fontSize: 18, fontWeight: "800" }}>
                {zoomArtwork?.title}
              </Text>
              <TouchableOpacity
                onPress={() => setZoomArtwork(null)}
                accessibilityLabel={t("Close enlarged artwork")}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                  borderRadius: 9,
                  borderWidth: 1,
                  borderColor: c.border,
                }}
              >
                <Text style={{ color: c.text, fontWeight: "800" }}>
                  {t("Close")}
                </Text>
              </TouchableOpacity>
            </View>

            {!!zoomArtwork && (
              <Image
                source={{
                  uri: getJourneyAssetUrl(zoomArtwork.imagePath),
                }}
                accessibilityLabel={zoomArtwork.accessibleDescription}
                resizeMode="contain"
                style={{
                  width: "100%",
                  height: 620,
                  backgroundColor: c.panel,
                }}
              />
            )}
          </View>
        </View>
      </Modal>

      <View
        style={{
          alignSelf: "flex-start",
          paddingHorizontal: 11,
          paddingVertical: 6,
          borderRadius: 999,
          backgroundColor: c.mysterySoft,
          borderWidth: 1,
          borderColor: c.mysteryBorder,
        }}
      >
        <Text
          style={{
            color: c.mystery,
            fontSize: 12,
            fontWeight: "900",
            letterSpacing: 1.1,
          }}
        >
          {t("CASE FILE 01")}
        </Text>
      </View>

      <Text
        style={{
          color: c.text,
          fontSize: 30,
          fontWeight: "900",
          lineHeight: 37,
          marginTop: 12,
        }}
      >
        {t("The Mysterious Artwork")}
      </Text>

      <LegacyInstructionalFlowSummary c={c} t={t} />

      <LegacyInstructionalModeStrip
        t={t}
        mode="I_DO"
        studentCue={t(
          "Listen as the teacher introduces the mystery. Notice the clues, but do not solve the case yet."
        )}
        c={c}
        marginTop={16}
      />

      <View
        style={{
          marginTop: 16,
          padding: 20,
          borderRadius: 16,
          backgroundColor: c.story,
          borderWidth: 1,
          borderColor: c.storyBorder,
        }}
      >
        <Text
          style={{
            color: c.text,
            fontSize: 18,
            fontWeight: "800",
            lineHeight: 27,
          }}
        >
          {t("Something strange happened in the art room.")}
        </Text>

        <Text
          style={{
            color: c.text,
            fontSize: 16,
            lineHeight: 25,
            marginTop: 10,
          }}
        >
          {t(
            "Four drawings were found lying on a table—but no one knows who made them."
          )}
        </Text>

        <Text
          style={{
            color: c.text,
            fontSize: 16,
            lineHeight: 25,
            marginTop: 8,
          }}
        >
          {t(
            "Some contain loops, spirals, tangled lines, and patterns that repeat again and again. Another looks very different."
          )}
        </Text>

        <Text
          style={{
            color: c.text,
            fontSize: 16,
            lineHeight: 25,
            marginTop: 8,
          }}
        >
          {t(
            "Was every picture made in the same way? Your mission is to study the artwork, search for clues, and develop a theory."
          )}
        </Text>
      </View>

      <View style={{ marginTop: 30 }}>
        <LegacyInstructionalModeStrip
          t={t}
          mode="WE_DO"
          studentCue={t(
            "Study the artwork together and point to visible evidence before choosing an explanation."
          )}
          c={c}
        />

        <StepSectionTitle c={c} icon="🔎">
          {t("Study the Evidence")}
        </StepSectionTitle>

        <Text
          style={{
            color: c.muted,
            fontSize: 15,
            lineHeight: 23,
            marginBottom: 15,
          }}
        >
          {t(
            "Open each artwork and look carefully. Do not decide too quickly—a good investigator notices details before forming a conclusion."
          )}
        </Text>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {STEP_ONE_ARTWORKS.map((artwork) => {
            const selected = selectedArtworkId === artwork.id;

            return (
              <TouchableOpacity
                key={artwork.id}
                onPress={() => {
                  setSelectedArtworkId(artwork.id);
                  setZoomArtwork(artwork);
                }}
                accessibilityLabel={t("Open {title}", { title: artwork.title })}
                style={{
                  width: "48%",
                  minWidth: 280,
                  flexGrow: 1,
                  borderRadius: 16,
                  overflow: "hidden",
                  backgroundColor: c.card,
                  borderWidth: selected ? 3 : 1,
                  borderColor: selected ? c.accent : c.border,
                }}
              >
                <Image
                  source={{
                    uri: getJourneyAssetUrl(artwork.imagePath),
                  }}
                  accessibilityLabel={artwork.accessibleDescription}
                  resizeMode="cover"
                  style={{
                    width: "100%",
                    aspectRatio: 4 / 3,
                    backgroundColor: c.panel,
                  }}
                />

                <View
                  style={{
                    paddingHorizontal: 14,
                    paddingVertical: 12,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        color: c.text,
                        fontSize: 17,
                        fontWeight: "800",
                      }}
                    >
                      {artwork.title}
                    </Text>
                    <Text
                      style={{
                        color: c.muted,
                        fontSize: 13,
                        lineHeight: 18,
                        marginTop: 2,
                      }}
                    >
                      {selected
                        ? t("Selected for your investigation")
                        : t("Tap to examine closely")}
                    </Text>
                  </View>

                  <Text style={{ color: c.accent, fontSize: 20 }}>
                    {selected ? "✓" : "↗"}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View
        style={{
          marginTop: 26,
          padding: 18,
          borderRadius: 14,
          backgroundColor: c.tip,
          borderWidth: 1,
          borderColor: c.tipBorder,
        }}
      >
        <StepSectionTitle c={c} icon="🕵️">
          {t("Investigator's Tip")}
        </StepSectionTitle>

        <Text style={{ color: c.text, fontSize: 15, lineHeight: 23 }}>
          {t("Look for clues such as:")}
        </Text>

        {[
          "shapes or lines that repeat",
          "smooth lines and shaky lines",
          "places where lines cross",
          "lines that seem to continue without stopping",
          "recognizable objects",
          "areas that appear carefully planned",
          "patterns that may have happened accidentally",
        ].map((tip) => (
          <Text
            key={tip}
            style={{
              color: c.text,
              fontSize: 15,
              lineHeight: 23,
              marginTop: 5,
            }}
          >
            {"•"} {t(tip)}
          </Text>
        ))}
      </View>

      <View style={{ marginTop: 30 }}>
        <LegacyInstructionalModeStrip
          t={t}
          mode="YOU_DO"
          studentCue={t(
            "Record your own clues, comparison, and theory using evidence from the artwork."
          )}
          c={c}
        />

        <StepSectionTitle c={c} icon="📝">
          {t("What Do You Notice?")}
        </StepSectionTitle>

        <Text style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
          {t(
            "Record at least three clues. Describe exactly what you see instead of only saying that a picture looks strange."
          )}
        </Text>

        {observations.map((observation, index) => (
          <View key={`observation-${index}`} style={{ marginTop: 12 }}>
            <Text
              style={{
                color: c.text,
                fontSize: 14,
                fontWeight: "800",
                marginBottom: 6,
              }}
            >
              {t("Clue")} {index + 1}
            </Text>
            <TextInput
              value={observation}
              onChangeText={(value) => updateObservation(index, value)}
              placeholder={t(
                index === 0
                  ? "I notice..."
                  : index === 1
                    ? "Another clue is..."
                    : "I also notice..."
              )}
              placeholderTextColor={c.placeholder}
              multiline
              editable={!readOnly}
              accessibilityLabel={`${t("Clue")} ${index + 1}`}
              style={{
                minHeight: 64,
                color: c.text,
                fontSize: 15,
                lineHeight: 22,
                textAlignVertical: "top",
                paddingHorizontal: 13,
                paddingVertical: 11,
                borderRadius: 11,
                borderWidth: 1,
                borderColor: observation.trim() ? c.accent : c.border,
                backgroundColor: c.input,
              }}
            />
          </View>
        ))}

        <Text
          style={{
            color:
              completedObservationCount >= 3 ? c.success : c.muted,
            fontSize: 13,
            fontWeight: "700",
            marginTop: 9,
          }}
        >
          {completedObservationCount}/3 {t("clues recorded")}
        </Text>
      </View>

      <View style={{ marginTop: 30 }}>
        <StepSectionTitle c={c} icon="🧩">
          {t("Which Artwork Is Different?")}
        </StepSectionTitle>

        <Text style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
          {t(
            "Choose the artwork that seems most different from the others. There may be more than one reasonable answer."
          )}
        </Text>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 14,
          }}
        >
          {STEP_ONE_ARTWORKS.map((artwork) => {
            const chosen = differentArtworkId === artwork.id;

            return (
              <TouchableOpacity
                key={`different-${artwork.id}`}
                onPress={() => {
                  if (!readOnly) setDifferentArtworkId(artwork.id);
                }}
                disabled={readOnly}
                accessibilityLabel={`${t("Choose")} ${artwork.title}`}
                style={{
                  minWidth: 82,
                  paddingHorizontal: 17,
                  paddingVertical: 12,
                  borderRadius: 11,
                  borderWidth: 2,
                  borderColor: chosen ? c.accent : c.border,
                  backgroundColor: chosen ? c.accentSoft : c.card,
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    color: chosen ? c.accent : c.text,
                    fontSize: 16,
                    fontWeight: "900",
                  }}
                >
                  {artwork.title}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <TextInput
          value={differenceReason}
          onChangeText={setDifferenceReason}
          placeholder={t("What makes it different?")}
          placeholderTextColor={c.placeholder}
          multiline
          editable={!readOnly}
          accessibilityLabel={t("Explain which artwork is different")}
          style={{
            minHeight: 86,
            color: c.text,
            fontSize: 15,
            lineHeight: 22,
            textAlignVertical: "top",
            marginTop: 13,
            paddingHorizontal: 13,
            paddingVertical: 11,
            borderRadius: 11,
            borderWidth: 1,
            borderColor: differenceReason.trim() ? c.accent : c.border,
            backgroundColor: c.input,
          }}
        />
      </View>

      <View style={{ marginTop: 30 }}>
        <StepSectionTitle c={c} icon="💡">
          {t("What Is Your Theory?")}
        </StepSectionTitle>

        <Text style={{ color: c.muted, fontSize: 15, lineHeight: 23 }}>
          {t(
            "How do you think these artworks were created? You may have more than one idea. Use at least one clue from the pictures as evidence."
          )}
        </Text>

        <TextInput
          value={theory}
          onChangeText={setTheory}
          placeholder={t("My theory is... I think this because...")}
          placeholderTextColor={c.placeholder}
          multiline
          editable={!readOnly}
          accessibilityLabel={t("Enter your theory")}
          style={{
            minHeight: 116,
            color: c.text,
            fontSize: 15,
            lineHeight: 22,
            textAlignVertical: "top",
            marginTop: 13,
            paddingHorizontal: 13,
            paddingVertical: 11,
            borderRadius: 11,
            borderWidth: 1,
            borderColor: theory.trim() ? c.accent : c.border,
            backgroundColor: c.input,
          }}
        />

        <Text
          style={{
            color: c.muted,
            fontSize: 13,
            lineHeight: 19,
            marginTop: 8,
          }}
        >
          {t(
            "You do not need to solve the mystery yet. A strong theory is supported by careful observations."
          )}
        </Text>
      </View>

      <View
        style={{
          marginTop: 30,
          padding: 19,
          borderRadius: 16,
          backgroundColor: c.ai,
          borderWidth: 1,
          borderColor: c.aiBorder,
        }}
      >
        <StepSectionTitle c={c} icon="✨">
          {t("Investigate with Saras")}
        </StepSectionTitle>

        <Text style={{ color: c.text, fontSize: 15, lineHeight: 23 }}>
          {t(
            "Ask Saras to help you compare the pictures. Saras will help you investigate—not give away the answer."
          )}
        </Text>

        {!!selectedArtwork && (
          <View
            style={{
              alignSelf: "flex-start",
              marginTop: 11,
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 999,
              backgroundColor: c.accentSoft,
              borderWidth: 1,
              borderColor: c.accent,
            }}
          >
            <Text
              style={{
                color: c.accent,
                fontSize: 12,
                fontWeight: "800",
              }}
            >
              {t("Visual context")}: {selectedArtwork.title}
            </Text>
          </View>
        )}

        <View style={{ marginTop: 12, gap: 9 }}>
          {STEP_ONE_SUGGESTED_QUESTIONS.map((question) => (
            <TouchableOpacity
              key={question}
              onPress={() => askSarasFromStepOne(question)}
              style={{
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: 11,
                backgroundColor: c.card,
                borderWidth: 1,
                borderColor: c.aiBorder,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <Text
                style={{
                  flex: 1,
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 22,
                }}
              >
                {question}
              </Text>
              <Text style={{ color: c.accent, fontWeight: "900" }}>{"→"}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View
        style={{
          marginTop: 30,
          padding: 20,
          borderRadius: 16,
          backgroundColor: c.caseUpdated,
          borderWidth: 1,
          borderColor: c.caseUpdatedBorder,
        }}
      >
        <Text
          style={{
            color: c.text,
            fontSize: 18,
            fontWeight: "900",
          }}
        >
          {t("Case file updated.")}
        </Text>
        <Text
          style={{
            color: c.text,
            fontSize: 15,
            lineHeight: 23,
            marginTop: 7,
          }}
        >
          {t(
            "You found important clues and formed your first theory. Next, discover who—or what—created the mysterious artwork."
          )}
        </Text>
      </View>
    </>
  );
}

/**
 * Drawing Robot Journey prototype.
 *
 * Step 1 has a custom student-facing mystery experience.
 * Other steps retain the generic runtime renderer while they are developed.
 */
export default function JourneyPanel({
  journey,
  authToken,
  lang,
  isDarkMode = false,
  handleVideoClick,
  handlePWOClick,
  onAsk,
  onContextChange,
  activeStepIndex: controlledActiveStepIndex,
  onStepChange,

  sessionState,
  activityCursorRef,
  onActivityCursorChange,
  onSessionStateChange,
  readOnly = false,

  // Classroom presentation reuses the authored Journey surface and removes
  // workspace chrome. Editability is still governed by `readOnly`: teachers may
  // edit their own Journey while a selected student's Journey stays read-only.
  // `onPresent` is supplied only for teachers.
  presentationMode = false,
  compactSelfGuidedLayout = false,
  wonderLabsDensity = 1,
  onPresent,
  journeyMode = "teacher_led",
  onJourneyModeChange,
  onPageCompletionChange,
  onActivityNavigationChange,
  onOpenJourneyHelp,
  onSelfGuidedCoachChange,
  onMasterTeacherReadAloud,
  swanReadAloudEnabled = false,
  onClose,
  t = (value) => value,
}) {
  const steps = useMemo(() => {
    if (Array.isArray(journey?.steps)) return journey.steps;
    if (Array.isArray(journey?.runtime?.steps)) return journey.runtime.steps;
    return [];
  }, [journey]);

  const [internalActiveStepIndex, setInternalActiveStepIndex] =
    useState(0);

  const isStepControlled = Number.isInteger(
    controlledActiveStepIndex
  );

  const requestedStepIndex = isStepControlled
    ? controlledActiveStepIndex
    : internalActiveStepIndex;

  const maximumStepIndex = Math.max(0, steps.length - 1);

  const activeStepIndex = Math.max(
    0,
    Math.min(maximumStepIndex, requestedStepIndex || 0)
  );

  const setActiveStepIndex = (nextValue, details) => {
    const requestedValue =
      typeof nextValue === "function"
        ? nextValue(activeStepIndex)
        : nextValue;

    const nextIndex = Math.max(
      0,
      Math.min(
        maximumStepIndex,
        Number.isFinite(Number(requestedValue))
          ? Number(requestedValue)
          : activeStepIndex
      )
    );

    if (!isStepControlled) {
      setInternalActiveStepIndex(nextIndex);
    }

    onStepChange?.(nextIndex, details);
  };

  const activeStep = steps[activeStepIndex] || null;

  const activeStepId = String(
    activeStep?.step_id || activeStep?.id || ""
  );
  // V2 evidence-recovery bookmark exists only for this mounted Journey.
  // The original phase, authored answers and completion rules are unchanged.
  const [evidenceRecoveryReturnTo, setEvidenceRecoveryReturnTo] = useState(null);
  const requestEvidenceRecovery = (targetStepId) => {
    if (journeyMode !== "self_guided" ||
        !["flat-bridge-test", "engineer-bridge-two", "fair-rescue-test", "rescue-prediction"].includes(targetStepId)) return;
    const targetIndex = steps.findIndex((candidate) =>
      String(candidate?.step_id || candidate?.id || "") === targetStepId);
    if (targetIndex < 0) return;
    setEvidenceRecoveryReturnTo(activeStepId);
    setActiveStepIndex(targetIndex, { intent: "evidence-recovery" });
  };
  const returnFromEvidenceRecovery = () => {
    const targetIndex = steps.findIndex((candidate) =>
      String(candidate?.step_id || candidate?.id || "") === evidenceRecoveryReturnTo);
    if (targetIndex < 0) return;
    setEvidenceRecoveryReturnTo(null);
    setActiveStepIndex(targetIndex, { intent: "evidence-recovery" });
  };

  const activeJourneySlug = String(
    journey?.slug ||
      journey?.runtime?.slug ||
      journey?.manifest?.slug ||
      journey?.manifest_json?.journey?.slug ||
      ""
  )
    .trim()
    .toLowerCase();

  const isDrawingRobotJourney = [
    "mystery-of-the-robot-artist",
    "drawing-robot",
  ].includes(activeJourneySlug);

  const contentScrollRef = useRef(null);
  const lastFocusedActivityRef = useRef(null);
  const registerFocusedActivity = useCallback((activity) => {
    if (!onActivityNavigationChange) return;
    if (!activity) {
      lastFocusedActivityRef.current = null;
      onActivityNavigationChange(null);
      return;
    }
    const resetScroll = () => contentScrollRef.current?.scrollTo?.({ y: 0, animated: false });
    // Also reset after direct activity-tab navigation. Only an actual activity
    // switch resets scrolling: entering an answer/checking a box never does.
    const identity = `${activity.stepId}:${activity.index}`;
    if (lastFocusedActivityRef.current !== identity) {
      lastFocusedActivityRef.current = identity;
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(resetScroll);
      else resetScroll();
    }
    onActivityNavigationChange({
      ...activity,
      advance: () => { activity.advance?.(); resetScroll(); },
      retreat: () => { activity.retreat?.(); resetScroll(); },
    });
  }, [onActivityNavigationChange]);
  const scrollContentHeightRef = useRef(0);
  const scrollViewportHeightRef = useRef(0);

  const scrollStorageKey = `${
    activeJourneySlug || journey?.id || "journey"
  }:${activeStepId || activeStepIndex}`;

  // Pending restore for the current step. `settled` flips true once the
  // ScrollView reaches the saved offset (or the offset is unreachable and the
  // user takes over); only settled positions are recorded as the user's.
  const scrollRestoreRef = useRef({
    key: null,
    target: 0,
    settled: true,
  });

  if (scrollRestoreRef.current.key !== scrollStorageKey) {
    scrollRestoreRef.current = {
      key: scrollStorageKey,
      target: stepScrollOffsets.get(scrollStorageKey) ?? 0,
      settled: false,
    };
  }

  const applyPendingScrollRestore = () => {
    const restore = scrollRestoreRef.current;
    if (restore.settled) return;
    contentScrollRef.current?.scrollTo({
      y: restore.target,
      animated: false,
    });
    if (restore.target === 0) {
      restore.settled = true;
    }
  };

  useEffect(() => {
    applyPendingScrollRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollStorageKey]);

  const handleContentScroll = (event) => {
    const offsetY = event?.nativeEvent?.contentOffset?.y ?? 0;
    const restore = scrollRestoreRef.current;
    if (!restore.settled) {
      const maxOffset = Math.max(
        0,
        scrollContentHeightRef.current -
          scrollViewportHeightRef.current
      );
      const reachableTarget = Math.min(restore.target, maxOffset);
      if (Math.abs(offsetY - reachableTarget) > 2) {
        // Clamped or intermediate position while content is still laying
        // out; keep the saved offset so a later layout pass can finish.
        return;
      }
      restore.settled = true;
    }
    stepScrollOffsets.set(scrollStorageKey, offsetY);
  };

  const handleContentScrollLayout = (event) => {
    scrollViewportHeightRef.current =
      event?.nativeEvent?.layout?.height ?? 0;
    applyPendingScrollRestore();
  };

  const handleContentSizeChange = (contentWidth, contentHeight) => {
    scrollContentHeightRef.current = contentHeight ?? 0;
    applyPendingScrollRestore();
  };

  // The registry builds a fresh page object per call; memoize so consumers
  // (e.g. JourneyPageRenderer's contextPayload effect) see a stable reference.
  const authoredJourneyPage = useMemo(
    () =>
      getAuthoredJourneyPage({
        journeySlug: activeJourneySlug,
        stepId: activeStepId,
        lang,
      }),
    [activeJourneySlug, activeStepId, lang]
  );

  const isSelfGuidedJourney = journeyMode === "self_guided";

  const guidedJourneyPage = useMemo(() => {
    if (!authoredJourneyPage || !isSelfGuidedJourney) return authoredJourneyPage;

    const professorSwanContext =
      activeJourneySlug === "magnetworks-mission"
        ? "SELF-GUIDED MAGNETWORKS (ages 5–7): Coach one short hands-on investigation at a time, using actual saved predictions and CHILD-REPORTED observations. Never invent magnet movement. Keep small ball and detachable magnets away from mouths, ears, noses and younger siblings; request help for loose discs, damaged magnets or cardboard cutting, not every ordinary step. The car travels ALONG its side of the wall, not through it. Pole-face changes may reverse magnetic interaction, but friction, orientation and placement matter; colors are not pole labels. PWO 449 shows iron filings revealing field patterns after real trials. Optional PWO 450 is a motor powered by current: its Current (mA) slider starts at 0, so prompt the learner to move it a little ABOVE zero to see motion, then back to zero. Do not confuse the motor with this battery-free physical kit. Protect predictions and give one actionable hint at a time."
        : activeJourneySlug === "food-delivery-robot-mission"
        ? "SELF-GUIDED MODE: You are Professor Swan helping an Explorer using a manufacturer-labeled 6+ kit. The child leads the normal assembly, experiments and investigation. Do NOT invent routine grown-up supervision for screws, battery placement, the power lever, or ordinary kit actions. Give one short, actionable spoken instruction at a time; coach troubleshooting rather than referring the learner away. Keep batteries out for wiring or mechanical changes; stop and seek hands-on adult assistance only for a specific genuine hazard such as a damaged or hot part, exposed unsafe wiring, sharp edges or an adjustment needing excessive force. Never assume the model or a checklist proves the real build works. Honor any actual manufacturer safety instruction; distinguish the child's predictions from reported observations."
        : activeJourneySlug === "robot-artist-explorers-mission"
          ? "SELF-GUIDED MODE: You are Professor Swan helping a 6–7-year-old Explorer. Give short, clear, friendly spoken-style instructions and one action at a time. Preserve the authored mystery reveal. Encourage the child to predict, observe, test, and explain independently. The parent assists with small screws, loose wires, batteries, and powered assembly checks. Never invent or claim to have seen a physical result. Offer useful progressive hints and direct explanation when needed. The child may say not sure or report that the robot did not work. Never force unsafe testing."
        : "SELF-GUIDED MODE: You are Professor Swan, the student's first source of teaching, assembly explanation, and troubleshooting help. Use existing videos, rotatable PWOs, and kit instructions. Never claim to have physically inspected a kit or approve a physical connection you cannot see. Ask for adult assistance only for hot/damaged parts, tools or unfamiliar physical tasks the child cannot safely manage. You are a warm STEM instructor for a grade 3-6 learner working mostly independently at home. Coach exactly one step at a time. Default to 2-4 short sentences, then ask at most one clear question or give one clear action. Use concrete child-friendly language. Avoid unnecessary analogies, long explanations, and multiple questions in one turn. Protect safety and the authored reveal policy. Praise evidence and persistence without overpraising. Never do the learner's required observation, design decision, measurement, or written explanation for them. If the learner is stuck, use the smallest useful hint first.";

    const floodRescueScienceGuardrails =
      activeJourneySlug === "flood-rescue-bridge"
        ? "FLOOD RESCUE BRIDGE SCIENCE GUARDRAILS: Keep observation separate from explanation. Do not call the place that bent most the 'weakest part' unless the student's evidence actually establishes material weakness. Explain simply that where bending is greatest depends on the load and support arrangement. Before Bridge 1 evidence is collected, do not suggest folds, tubes, arches, corrugation, accordion shapes, or a strongest design. During redesign, coach from the student's own evidence rather than prescribing a shape. For fair tests, reinforce changing one variable while keeping supports, paper amount, loading method, and load placement consistent."
        : "";
    const homeMission = HOME_STEM_MISSIONS.has(activeJourneySlug);
    const missionGuardrails = HOME_STEM_GUARDRAILS[activeJourneySlug] || "";

    const blockGuidance =
      authoredJourneyPage?.self_guided_block_guidance ||
      authoredJourneyPage?.self_guided?.block_guidance ||
      {};

    return {
      ...authoredJourneyPage,
      // First child-guided pilot: Paper Bridge EXPLORE. Future required work
      // is revealed only after the current required activity is finished.
      self_guided_progressive:
        activeJourneySlug === "flood-rescue-bridge",
      self_guided_progress_labels:
        activeJourneySlug === "flood-rescue-bridge"
          ? ({
              "flood-rescue-prelude": {
                mission_fact_check: "Answer the mission fact check",
              },
              "rescue-prediction": {
                bridge1_prediction: "Make your rescue prediction",
              },
              "flat-bridge-test": {
                bridge1_setup: "Check your bridge setup",
                bridge1_record: "Record what happened",
                bridge1_direct_observation: "Write one clear observation",
              },
              "hidden-forces": {
                support_force_check: "Answer the force check",
                bridge1_force_explanation: "Explain why Bridge 1 bent",
              },
              "engineer-bridge-two": {
                bridge2_design_change: "Describe your Bridge 2 change",
                bridge2_design_prediction: "Explain why your change might help",
              },
              "fair-rescue-test": {
                fair_test_constants: "Check your fair-test setup",
                bridge2_record: "Record Bridge 2 results",
                bridge_comparison_claim: "Compare Bridge 1 and Bridge 2",
              },
              "young-engineer-report": {
                report_bridge1_failure: "Explain what happened to Bridge 1",
                report_design_change: "Explain what you changed",
                report_evidence_defense: "Use evidence to support your conclusion",
              },
              "evidence-reflection": {
                evidence_changed_thinking: "Tell what changed your thinking",
                failure_learning_reflection: "Tell what failure taught you",
              },
              "next-variable-mission": {
                next_variable_choice: "Choose what you want to test next",
                next_test_plan: "Plan your next fair test",
              },
            }[activeStepId] || authoredJourneyPage?.self_guided_progress_labels)
          : authoredJourneyPage?.self_guided_progress_labels,
      instructional_flow: authoredJourneyPage?.instructional_flow
        ? {
            ...authoredJourneyPage.instructional_flow,
            self_guided_active: true,
          }
        : authoredJourneyPage?.instructional_flow,
      blocks: Array.isArray(authoredJourneyPage.blocks)
        ? authoredJourneyPage.blocks.map((authoredBlock) => {
            const block = homeMission
              ? adaptBlockCopyForHome(authoredBlock)
              : authoredBlock;
            return {
            ...applySelfGuidedTeachingCopy(block),
            self_guided_active: true,
            self_guided_guidance:
              block?.self_guided_guidance || blockGuidance?.[block?.id] || null,
            student_cue: block?.self_guided_cue || block?.student_cue,
            title:
              block?.type === "saras_checkpoint"
                ? String(block?.self_guided_title || block?.title || "")
                    .replace(/Saras/g, "Professor Swan")
                : block?.title,
            hidden_context:
              block?.type === "saras_checkpoint"
                ? `${professorSwanContext}\n\n${floodRescueScienceGuardrails}\n\n${missionGuardrails}\n\n${String(block?.hidden_context || "").trim()}`.trim()
                : block?.hidden_context,
            };
          })
        : authoredJourneyPage.blocks,
    };
  }, [
    authoredJourneyPage,
    isSelfGuidedJourney,
    activeJourneySlug,
    activeStepId,
  ]);

  // Step 1 demo state. It intentionally persists while the student moves
  // between Journey steps during the current session.
  const [internalJourneyState, setInternalJourneyState] =
    useState(() => ({
      selectedArtworkId: null,
      observations: ["", "", ""],
      differentArtworkId: null,
      differenceReason: "",
      theory: "",
      steps: {},
    }));

  const hasControlledSessionState =
    sessionState &&
    typeof sessionState === "object";

  const effectiveJourneyState = hasControlledSessionState
    ? sessionState
    : internalJourneyState;

  const updateJourneyState = (updater) => {
    if (readOnly) return;
    if (hasControlledSessionState) {
      onSessionStateChange?.(updater);
    } else {
      setInternalJourneyState(updater);
    }
  };

  const setJourneyField = (field) => (nextValue) => {
    updateJourneyState((current) => {
      const safeCurrent = current || {};

      const resolvedValue =
        typeof nextValue === "function"
          ? nextValue(safeCurrent[field])
          : nextValue;

      return {
        ...safeCurrent,
        [field]: resolvedValue,
      };
    });
  };

  const selectedArtworkId =
    effectiveJourneyState?.selectedArtworkId ?? null;

  const observations =
    Array.isArray(effectiveJourneyState?.observations)
      ? effectiveJourneyState.observations
      : ["", "", ""];

  const differentArtworkId =
    effectiveJourneyState?.differentArtworkId ?? null;

  const differenceReason =
    effectiveJourneyState?.differenceReason ?? "";

  const theory =
    effectiveJourneyState?.theory ?? "";

  const setSelectedArtworkId =
    setJourneyField("selectedArtworkId");

  const setObservations =
    setJourneyField("observations");

  const setDifferentArtworkId =
    setJourneyField("differentArtworkId");

  const setDifferenceReason =
    setJourneyField("differenceReason");

  const setTheory =
    setJourneyField("theory");

  // Image enlargement is temporary UI state and can remain local.
  const [zoomArtwork, setZoomArtwork] = useState(null);


  // Clear Journey context only for steps that use neither the
  // custom Step 1 experience nor the reusable authored-page renderer.
  useEffect(() => {
    if (
      !(
        isDrawingRobotJourney &&
        activeStepId === "mysterious-artwork"
      ) &&
      !authoredJourneyPage
    ) {
      onContextChange?.(null);
    }
  }, [
    activeStepId,
    authoredJourneyPage,
    isDrawingRobotJourney,
    onContextChange,
  ]);

  const c = {
    bg: isDarkMode ? "#0f172a" : "#ffffff",
    panel: isDarkMode ? "#111827" : "#f8fafc",
    card: isDarkMode ? "#111827" : "#ffffff",
    input: isDarkMode ? "#0b1220" : "#ffffff",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    placeholder: isDarkMode ? "#64748b" : "#94a3b8",
    border: isDarkMode ? "rgba(148,163,184,0.24)" : "#dbe4ee",
    link: isDarkMode ? "#60a5fa" : "#2563eb",
    accent: isDarkMode ? "#38bdf8" : "#0369a1",
    accentSoft: isDarkMode ? "rgba(56,189,248,0.12)" : "#e0f2fe",
    success: isDarkMode ? "#4ade80" : "#15803d",
    task: isDarkMode ? "rgba(251,191,36,0.10)" : "#fffbeb",
    taskBorder: isDarkMode ? "rgba(251,191,36,0.25)" : "#fde68a",
    mystery: isDarkMode ? "#c4b5fd" : "#6d28d9",
    mysterySoft: isDarkMode ? "rgba(139,92,246,0.13)" : "#f5f3ff",
    mysteryBorder: isDarkMode ? "rgba(167,139,250,0.35)" : "#ddd6fe",
    story: isDarkMode ? "rgba(30,41,59,0.78)" : "#f8fafc",
    storyBorder: isDarkMode ? "rgba(148,163,184,0.24)" : "#dbeafe",
    tip: isDarkMode ? "rgba(245,158,11,0.10)" : "#fffbeb",
    tipBorder: isDarkMode ? "rgba(245,158,11,0.30)" : "#fde68a",
    ai: isDarkMode ? "rgba(124,58,237,0.10)" : "#faf5ff",
    aiBorder: isDarkMode ? "rgba(167,139,250,0.30)" : "#e9d5ff",
    caseUpdated: isDarkMode ? "rgba(14,116,144,0.14)" : "#ecfeff",
    caseUpdatedBorder: isDarkMode ? "rgba(34,211,238,0.28)" : "#a5f3fc",
    instructionalModes: {
      I_DO: {
        accent: isDarkMode ? "#ddd6fe" : "#6d28d9",
        soft: isDarkMode
          ? "rgba(139,92,246,0.15)"
          : "#f5f3ff",
        border: isDarkMode
          ? "rgba(196,181,253,0.42)"
          : "#c4b5fd",
      },
      WE_DO: {
        accent: isDarkMode ? "#5eead4" : "#0f766e",
        soft: isDarkMode
          ? "rgba(20,184,166,0.14)"
          : "#f0fdfa",
        border: isDarkMode
          ? "rgba(94,234,212,0.38)"
          : "#99f6e4",
      },
      YOU_DO: {
        accent: isDarkMode ? "#fbbf24" : "#b45309",
        soft: isDarkMode
          ? "rgba(245,158,11,0.14)"
          : "#fffbeb",
        border: isDarkMode
          ? "rgba(251,191,36,0.40)"
          : "#fcd34d",
      },
    },
  };

  const activityType = (a) =>
    String(a?.type || a?.activity_type || a?.kind || "").toLowerCase();

  const activityContent = (a) =>
    a?.content ||
    a?.resolved_asset ||
    a?.resolved ||
    a?.asset ||
    a?.asset_ref ||
    {};

  const activityLabel = (a, fallback) =>
    a?.title ||
    a?.display_name ||
    a?.name ||
    activityContent(a)?.display_name ||
    activityContent(a)?.name ||
    fallback;

  const activities = Array.isArray(activeStep?.activities)
    ? activeStep.activities
    : [];

  const videos = activities.filter((a) =>
    ["video", "saras_video"].includes(activityType(a))
  );

  const pwos = activities.filter((a) =>
    ["pwo", "saras_pwo", "3d", "simulation"].includes(activityType(a))
  );

  const quizzes = activities.filter((a) =>
    ["quiz", "question"].includes(activityType(a))
  );

  const aiPrompts = activities.filter((a) =>
    ["ai", "ask_saras", "prompt", "ai_prompt"].includes(activityType(a))
  );

  const studentActions = Array.isArray(activeStep?.student_actions)
    ? activeStep.student_actions
    : Array.isArray(activeStep?.actions)
      ? activeStep.actions
      : [];

  const openVideo = async (activity) => {
    const content = activityContent(activity);
    const videoID =
      content?.source_video_id ??
      content?.video_id ??
      content?.source_id ??
      activity?.source_video_id ??
      activity?.video_id ??
      null;

    const tagID =
      content?.tag_id ??
      content?.tagID ??
      activity?.tag_id ??
      activity?.tagID ??
      null;

    if (videoID == null) {
      console.warn("[JourneyPanel] Missing source_video_id", activity);
      return;
    }

    await handleVideoClick?.(
      videoID,
      tagID,
      authToken,
      null,
      content?.source_topic_id ?? activity?.source_topic_id ?? null,
      false,
      null,
      null,
      activityLabel(activity, t("Video"))
    );
  };

  const openPwo = async (activity) => {
    const content = activityContent(activity);
    const pwoID =
      content?.source_pwo_id ??
      content?.pwo_source_id ??
      content?.pwo_id ??
      content?.source_id ??
      activity?.source_pwo_id ??
      activity?.pwo_id ??
      null;

    const sceneName =
      content?.scene_name ??
      content?.pwo_scene_name ??
      content?.scene_key ??
      activity?.scene_name ??
      activity?.pwo_scene_name ??
      "mainmenu";

    if (pwoID == null) {
      console.warn("[JourneyPanel] Missing source_pwo_id", activity);
      return;
    }

    await handlePWOClick?.(
      pwoID,
      sceneName,
      authToken,
      lang,
      null,
      content?.source_topic_id ?? activity?.source_topic_id ?? null,
      false,
      null,
      null,
      { title: activity?.title, mission: activity?.description,
        activityId: activity?.activity_id }
    );
  };

  const askSaras = (prompt) => {
    const value =
      typeof prompt === "string"
        ? prompt
        : prompt?.prompt ||
          prompt?.text ||
          prompt?.question ||
          prompt?.title ||
          "";

    if (value.trim()) onAsk?.(value.trim());
  };

  if (!journey) {
    return (
      <View style={{ padding: 20 }}>
        <Text style={{ color: c.muted }}>{t("No Journey has been loaded.")}</Text>
      </View>
    );
  }

  if (!steps.length) {
    return (
      <View style={{ padding: 20 }}>
        <Text style={{ color: c.text, fontSize: 18, fontWeight: "700" }}>
          {journey?.title || journey?.name || t("Journey")}
        </Text>
        <Text style={{ color: c.muted, marginTop: 8 }}>
          {t("This Journey does not contain any steps.")}
        </Text>
      </View>
    );
  }

  const journeyTitle =
    journey?.title ||
    journey?.name ||
    journey?.manifest?.title ||
    t("Learning Journey");

  const journeyStory =
    journey?.story ||
    journey?.description ||
    journey?.manifest?.story ||
    "";

  const stepTitle =
    getAuthoredJourneyStepTitle({
      journeySlug: activeJourneySlug,
      stepId: activeStepId,
      lang,
    }) ||
    activeStep?.title ||
    activeStep?.name ||
    `${t("Step")} ${activeStepIndex + 1}`;

  const stepNarrative =
    activeStep?.narrative ||
    activeStep?.text ||
    activeStep?.description ||
    activeStep?.story ||
    activeStep?.instructions ||
    "";

  const isMysteriousArtworkStep =
    isDrawingRobotJourney &&
    activeStepId === "mysterious-artwork";

  const mysteriousArtworkPrelude =
    isMysteriousArtworkStep &&
    Array.isArray(authoredJourneyPage?.blocks)
      ? authoredJourneyPage.blocks.find(
          (block) => block.type === "story_prelude"
        ) || null
      : null;

  const isAuthoredJourneyPage =
    Boolean(guidedJourneyPage);

  const headerStory = isMysteriousArtworkStep
    ? t("Study the evidence, notice the clues, and form your first theory.")
    : guidedJourneyPage?.story || journeyStory;

  const isLastStep = steps.length > 0 && activeStepIndex === steps.length - 1;

  const goToNextStep = () => {
    if (isLastStep) {
      onClose?.();
      return;
    }

    setActiveStepIndex((index) => Math.min(steps.length - 1, index + 1));
  };

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: c.bg,
        borderWidth: presentationMode ? 0 : 1,
        borderColor: c.border,
        borderRadius: presentationMode ? 0 : 16,
        overflow: "hidden",
      }}
    >
      {!presentationMode && (
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 16,
          borderBottomWidth: 1,
          borderBottomColor: c.border,
          backgroundColor: c.panel,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ color: c.text, fontSize: 22, fontWeight: "800" }}>
              {journeyTitle}
            </Text>

            {!!headerStory && (
              <Text
                style={{
                  color: c.muted,
                  fontSize: 15,
                  lineHeight: 22,
                  marginTop: 6,
                }}
              >
                {headerStory}
              </Text>
            )}
          </View>

          {(!!onJourneyModeChange || !!onPresent || !!onClose) && (
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {!!onJourneyModeChange && (
                <TouchableOpacity
                  onPress={() => {
                    if (!isSelfGuidedJourney) {
                      primeReadAloud(lang || "en");
                    }
                    onJourneyModeChange(
                      isSelfGuidedJourney ? "teacher_led" : "self_guided"
                    );
                  }}
                  accessibilityLabel={
                    isSelfGuidedJourney
                      ? t("Leave Professor Swan guided mode")
                      : t("Learn with Professor Swan")
                  }
                  style={{
                    paddingVertical: 7,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: isSelfGuidedJourney ? c.success : c.accent,
                    backgroundColor: isSelfGuidedJourney
                      ? (isDarkMode ? "rgba(34,197,94,0.12)" : "#f0fdf4")
                      : c.accentSoft,
                  }}
                >
                  <Text style={{ color: isSelfGuidedJourney ? c.success : c.accent, fontWeight: "900" }}>
                    {"🦢"} {isSelfGuidedJourney ? t("Professor Swan On") : t("Learn with Professor Swan")}
                  </Text>
                </TouchableOpacity>
              )}

              {!!onPresent && (
                <TouchableOpacity
                  onPress={onPresent}
                  accessibilityLabel={t("Present Journey")}
                  style={{
                    paddingVertical: 7,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: c.accent,
                    backgroundColor: c.accentSoft,
                  }}
                >
                  <Text style={{ color: c.accent, fontWeight: "800" }}>
                    {"▣"} {t("Present")}
                  </Text>
                </TouchableOpacity>
              )}

              {!!onClose && (
                <TouchableOpacity
                  onPress={onClose}
                  accessibilityLabel={t("Close Journey")}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: c.border,
                  }}
                >
                  <Text style={{ color: c.text, fontWeight: "700" }}>
                    {t("Close")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>

      </View>
      )}

      <ScrollView
        ref={contentScrollRef}
        style={{ flex: 1 }}
        onScroll={handleContentScroll}
        scrollEventThrottle={16}
        onLayout={handleContentScrollLayout}
        onContentSizeChange={handleContentSizeChange}
        contentContainerStyle={{
          padding: presentationMode
            ? (compactSelfGuidedLayout ? 16 : 32)
            : isMysteriousArtworkStep || isAuthoredJourneyPage
              ? 26
              : 22,
          paddingBottom: presentationMode ? 48 : 38,
          maxWidth: presentationMode
            ? 1440
            : isMysteriousArtworkStep || isAuthoredJourneyPage
              ? 1180
              : undefined,
          width: "100%",
          alignSelf: compactSelfGuidedLayout ? "flex-start" : "center",
        }}
      >
        {isMysteriousArtworkStep ? (
          <View style={{ gap: 24 }}>
            {!!mysteriousArtworkPrelude && (
              <JourneyStoryPrelude
                block={mysteriousArtworkPrelude}
                c={c}
                t={t}
              />
            )}

            <MysteriousArtworkStep
            c={c}
            t={t}
            journey={journey}
            step={activeStep}
            observations={observations}
            setObservations={setObservations}
            selectedArtworkId={selectedArtworkId}
            setSelectedArtworkId={setSelectedArtworkId}
            zoomArtwork={zoomArtwork}
            setZoomArtwork={setZoomArtwork}
            differentArtworkId={differentArtworkId}
            setDifferentArtworkId={setDifferentArtworkId}
            differenceReason={differenceReason}
            setDifferenceReason={setDifferenceReason}
            theory={theory}
            setTheory={setTheory}
            onAsk={onAsk}
            onContextChange={onContextChange}
              readOnly={readOnly}
            />
          </View>
        ) : isAuthoredJourneyPage ? (
          <View>
            {isSelfGuidedJourney && !presentationMode && (
              <ProfessorSwanGuide
                page={authoredJourneyPage}
                stepIndex={activeStepIndex}
                stepCount={steps.length}
                onAsk={(prompt) => onAsk?.(prompt)}
                autoReadEnabled={swanReadAloudEnabled}
                language={lang || "en"}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}
            <JourneyPageRenderer
            page={guidedJourneyPage}
            journey={journey}
            step={activeStep}
            sessionState={effectiveJourneyState}
            activityCursorRef={activityCursorRef}
            onActivityCursorChange={onActivityCursorChange}
            onSessionStateChange={updateJourneyState}
            readOnly={readOnly}
            authToken={authToken}
            onOpenVideo={openVideo}
            onOpenPwo={openPwo}
            onAsk={onAsk}
            onOpenHelp={onOpenJourneyHelp}
            onContextChange={onContextChange}
            onCompletionChange={onPageCompletionChange}
            onCoachChange={onSelfGuidedCoachChange}
            onReadAloud={onMasterTeacherReadAloud}
            onActivityNavigationChange={registerFocusedActivity}
            isDarkMode={isDarkMode}
            compactSelfGuidedLayout={compactSelfGuidedLayout && isSelfGuidedJourney && presentationMode}
            compactLearningViewport={compactSelfGuidedLayout && isSelfGuidedJourney && wonderLabsDensity < 0.95}
            onRequestEvidenceRecovery={requestEvidenceRecovery}
            onReturnFromEvidenceRecovery={returnFromEvidenceRecovery}
            evidenceRecoveryReturnTo={evidenceRecoveryReturnTo}
            t={t}
          />
          </View>
        ) : (
          <>
            <Text style={{ color: c.text, fontSize: 24, fontWeight: "800" }}>
              {stepTitle}
            </Text>

            {!!stepNarrative && (
              <Text
                style={{
                  color: c.text,
                  fontSize: 16,
                  lineHeight: 25,
                  marginTop: 12,
                }}
              >
                {stepNarrative}
              </Text>
            )}

            {videos.length > 0 && (
              <View style={{ marginTop: 26 }}>
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 14,
                    fontWeight: "800",
                    marginBottom: 8,
                  }}
                >
                  {"🎬"} {t("Watch")}
                </Text>

                {videos.map((activity, index) => (
                  <TouchableOpacity
                    key={
                      activity?.activity_id || activity?.id || `video-${index}`
                    }
                    onPress={() => openVideo(activity)}
                    style={{
                      paddingVertical: 8,
                      borderBottomWidth: index < videos.length - 1 ? 1 : 0,
                      borderBottomColor: c.border,
                    }}
                  >
                    <Text
                      style={{
                        color: c.link,
                        fontSize: 16,
                        fontWeight: "700",
                        lineHeight: 23,
                      }}
                    >
                      {"🎞"} {activityLabel(activity, t("Video"))}
                    </Text>

                    {!!activity?.description && (
                      <Text
                        style={{
                          color: c.muted,
                          fontSize: 14,
                          lineHeight: 20,
                          marginTop: 3,
                        }}
                      >
                        {activity.description}
                      </Text>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {pwos.length > 0 && (
              <View style={{ marginTop: 26 }}>
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 14,
                    fontWeight: "800",
                    marginBottom: 8,
                  }}
                >
                  {"🎮"} {t("Explore in 3D")}
                </Text>

                {pwos.map((activity, index) => (
                  <TouchableOpacity
                    key={
                      activity?.activity_id || activity?.id || `pwo-${index}`
                    }
                    onPress={() => openPwo(activity)}
                    style={{
                      paddingVertical: 8,
                      borderBottomWidth: index < pwos.length - 1 ? 1 : 0,
                      borderBottomColor: c.border,
                    }}
                  >
                    <Text
                      style={{
                        color: c.link,
                        fontSize: 16,
                        fontWeight: "700",
                        lineHeight: 23,
                      }}
                    >
                      {"🎮"} {activityLabel(activity, t("3D Simulation"))}
                    </Text>

                    {!!activity?.description && (
                      <Text
                        style={{
                          color: c.muted,
                          fontSize: 14,
                          lineHeight: 20,
                          marginTop: 3,
                        }}
                      >
                        {activity.description}
                      </Text>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {aiPrompts.length > 0 && (
              <View style={{ marginTop: 26 }}>
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 14,
                    fontWeight: "800",
                    marginBottom: 8,
                  }}
                >
                  {"💬"} {t("Ask Saras")}
                </Text>

                {aiPrompts.map((prompt, index) => (
                  <TouchableOpacity
                    key={
                      prompt?.activity_id || prompt?.id || `prompt-${index}`
                    }
                    onPress={() => askSaras(prompt)}
                    style={{
                      paddingVertical: 8,
                      borderBottomWidth:
                        index < aiPrompts.length - 1 ? 1 : 0,
                      borderBottomColor: c.border,
                    }}
                  >
                    <Text
                      style={{ color: c.link, fontSize: 16, lineHeight: 23 }}
                    >
                      {"•"}{" "}
                      {activityLabel(prompt, t("Ask Saras a question"))}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {studentActions.length > 0 && (
              <View
                style={{
                  marginTop: 26,
                  padding: 16,
                  borderRadius: 12,
                  backgroundColor: c.task,
                  borderWidth: 1,
                  borderColor: c.taskBorder,
                }}
              >
                <Text
                  style={{
                    color: c.text,
                    fontSize: 15,
                    fontWeight: "800",
                    marginBottom: 8,
                  }}
                >
                  {"✏️"} {t("Your Task")}
                </Text>

                {studentActions.map((action, index) => {
                  const label =
                    typeof action === "string"
                      ? action
                      : action?.text ||
                        action?.prompt ||
                        action?.title ||
                        action?.description ||
                        `${t("Task")} ${index + 1}`;

                  return (
                    <Text
                      key={action?.action_id || action?.id || index}
                      style={{
                        color: c.text,
                        fontSize: 15,
                        lineHeight: 23,
                        marginTop: index === 0 ? 0 : 6,
                      }}
                    >
                      {"•"} {label}
                    </Text>
                  );
                })}
              </View>
            )}

            {quizzes.length > 0 && (
              <View style={{ marginTop: 26 }}>
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 14,
                    fontWeight: "800",
                    marginBottom: 8,
                  }}
                >
                  {"📝"} {t("Check Your Understanding")}
                </Text>

                {quizzes.map((activity, index) => (
                  <View
                    key={
                      activity?.activity_id || activity?.id || `quiz-${index}`
                    }
                    style={{ paddingVertical: 7 }}
                  >
                    <Text
                      style={{ color: c.text, fontSize: 16, lineHeight: 23 }}
                    >
                      {"•"} {activityLabel(activity, t("Quiz question"))}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </>
        )}
      </ScrollView>

      {!presentationMode && (
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 14,
          borderTopWidth: 1,
          borderTopColor: c.border,
          backgroundColor: c.panel,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <TouchableOpacity
          onPress={() => setActiveStepIndex((i) => Math.max(0, i - 1))}
          disabled={activeStepIndex === 0}
          style={{
            opacity: activeStepIndex === 0 ? 0.45 : 1,
            borderWidth: 1,
            borderColor: c.border,
            borderRadius: 10,
            paddingVertical: 10,
            paddingHorizontal: 14,
          }}
        >
          <Text style={{ color: c.text, fontWeight: "700" }}>
            {"←"} {t("Previous")}
          </Text>
        </TouchableOpacity>

        <Text style={{ color: c.muted, fontSize: 13 }}>
          {activeStepIndex + 1} / {steps.length}
        </Text>

        <TouchableOpacity
          onPress={goToNextStep}
          disabled={steps.length === 0 || (isLastStep && !onClose)}
          style={{
            opacity: steps.length === 0 || (isLastStep && !onClose) ? 0.45 : 1,
            borderRadius: 10,
            paddingVertical: 10,
            paddingHorizontal: 14,
            backgroundColor: c.accent,
          }}
        >
          <Text style={{ color: "#ffffff", fontWeight: "800" }}>
            {isLastStep
              ? t("Finish Journey")
              : isMysteriousArtworkStep
                ? t("Continue the Investigation")
                : t("Next Step")}{" "}
            {"→"}
          </Text>
        </TouchableOpacity>
      </View>
      )}
    </View>
  );
}
